import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import ShipmentsToolbar from './components/ShipmentsToolbar';
import ShipmentsTable from './components/ShipmentsTable';
import YoyAmzStockPopup from './yoy-components/YoyAmzStockPopup';
import YoyAwdUploadPopup from './yoy-components/YoyAwdUploadPopup';
import { useYoyAwdUpload } from './yoy-components/useYoyAwdUpload';
import { useAmazonMonthlyForecast } from './components/useAmazonMonthlyForecast';
import ConfirmPopup from './components/ConfirmPopup';
import AmazonSkuMappingPopup from './components/AmazonSkuMappingPopup';
import WalmartShipmentsTab from './WalmartShipmentsTab';
import './ShipmentsPlanningPage.css';

const STORAGE_KEY = 'sp_view_settings';
const TAB_STORAGE_KEY = 'sp_active_marketplace_tab';

const RANGE_TITLES = { v7: 'Avg Daily Sales (7d)', v14: 'Avg Daily Sales (14d)', actual: 'Actual Sales' };

const getDateNDaysAhead = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Aggregate FBA stock rows by ASIN (same logic as YOYForecast)
const aggregateStockDataFrontend = (data) => {
  const asinMap = new Map();
  data.forEach(row => {
    const asin = row.ASIN || row.asin || row.Asin || '';
    if (!asin || asin.trim() === '') return;
    const available          = parseInt(row.available || row.Available || 0) || 0;
    const inboundQuantity    = parseInt(row['inbound-quantity'] || row.inbound_quantity || row['Inbound Quantity'] || 0) || 0;
    const reservedFCTransfer = parseInt(row['fc-transfer'] || row['Reserved FC Transfer'] || row['reserved-fc-transfer'] || 0) || 0;
    const reservedFCProc     = parseInt(row['Reserved FC Processing'] || row['reserved-fc-processing'] || 0) || 0;
    if (asinMap.has(asin)) {
      const ex = asinMap.get(asin);
      asinMap.set(asin, {
        available: ex.available + available,
        inbound_quantity: ex.inbound_quantity + inboundQuantity,
        reserved_fc_transfer: ex.reserved_fc_transfer + reservedFCTransfer,
        reserved_fc_processing: ex.reserved_fc_processing + reservedFCProc,
        total_quantity: ex.available + ex.inbound_quantity + ex.reserved_fc_transfer + ex.reserved_fc_processing,
      });
    } else {
      asinMap.set(asin, {
        available,
        inbound_quantity: inboundQuantity,
        reserved_fc_transfer: reservedFCTransfer,
        reserved_fc_processing: reservedFCProc,
        total_quantity: available + inboundQuantity + reservedFCTransfer + reservedFCProc,
      });
    }
  });
  return Array.from(asinMap.entries()).map(([asin, v]) => ({
    id: asin, asin, ...v,
  }));
};

const ShipmentsPlanningPage = () => {
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem(TAB_STORAGE_KEY) || 'amazon'; } catch { return 'amazon'; }
  });
  const switchTab = (tab) => {
    setActiveTab(tab);
    try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch {}
  };

  const [products, setProducts]     = useState([]);
  const [brands, setBrands]         = useState([]);
  const [niches, setNiches]         = useState([]);
  const [dailyPlan, setDailyPlan]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [boxInputs, setBoxInputs]   = useState({});
  const [boxSaveStatus, setBoxSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [showClearConfirm, setShowClearConfirm]       = useState(false);
  const [showSkuMappingPopup, setShowSkuMappingPopup] = useState(false);
  const [velocityData, setVelocityData]         = useState({});
  const [actualSalesData, setActualSalesData]     = useState({});
  const [producedData, setProducedData]           = useState({});
  const [commentsData, setCommentsData]           = useState({});

  // Stock timestamps
  const [fbaUpdatedAt, setFbaUpdatedAt] = useState(null);
  const [awdUpdatedAt, setAwdUpdatedAt] = useState(null);

  // ── FBA stock upload state ───────────────────────────────────────────────
  const [showStockUploadPopup, setShowStockUploadPopup] = useState(false);
  const [selectedStockFile, setSelectedStockFile]       = useState(null);
  const [parsedStockData, setParsedStockData]           = useState([]);
  const [uploadStockStatus, setUploadStockStatus]       = useState(null);
  const stockFileInputRef = useRef(null);

  const handleStockFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setSelectedStockFile(file);
    setParsedStockData([]);
    setUploadStockStatus(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      const firstRow = jsonData[0] || {};
      const hasASIN = 'ASIN' in firstRow || 'asin' in firstRow || 'Asin' in firstRow;
      if (!hasASIN) {
        setUploadStockStatus({ type: 'error', message: 'File must contain ASIN column' });
        return;
      }
      setParsedStockData(aggregateStockDataFrontend(jsonData));
    };
    reader.readAsArrayBuffer(file);
  };

  const handleStockUploadSubmit = async () => {
    if (!selectedStockFile) {
      setUploadStockStatus({ type: 'error', message: 'Please select a file' });
      return;
    }
    if (parsedStockData.length === 0) {
      setUploadStockStatus({ type: 'error', message: 'No valid data found in the file' });
      return;
    }
    const formData = new FormData();
    formData.append('file', selectedStockFile);
    try {
      setUploadStockStatus({ type: 'loading', message: 'Uploading stock data...' });
      const { data: result } = await api.post(`/api/upload-fba-stock`, formData);
      setUploadStockStatus({ type: 'success', message: result.message || 'Stock data uploaded successfully' });
      fetchTimestamps();
      reloadProducts();
      setTimeout(() => {
        setShowStockUploadPopup(false);
        setSelectedStockFile(null);
        setParsedStockData([]);
        setUploadStockStatus(null);
        if (stockFileInputRef.current) stockFileInputRef.current.value = '';
      }, 3000);
    } catch (err) {
      if (err.response) {
        setUploadStockStatus({ type: 'error', message: err.response.data?.error || 'Upload failed' });
      } else {
        setUploadStockStatus({ type: 'error', message: `Error: ${err.message}` });
      }
    }
  };

  // ── AWD stock upload hook ───────────────────────────────────────────────
  const awdUpload = useYoyAwdUpload(() => {
    fetchTimestamps();
    reloadProducts();
  });

  // Velocity и Actual Sales с 31.08.2026 считаются сервером из API-продаж
  // (amazon_week_sales) — попапы ручного сохранения удалены.

  // ── Data loading ────────────────────────────────────────────────────────
  const loadSettings = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  };
  const savedSettings = loadSettings();

  const [searchQuery, setSearchQuery]     = useState(savedSettings.searchQuery || '');
  const [selectedBrands, setSelectedBrands] = useState(savedSettings.selectedBrands || []);
  const [selectedNiches, setSelectedNiches] = useState(savedSettings.selectedNiches || []);
  const [selectedTypes, setSelectedTypes]   = useState(savedSettings.selectedTypes || []);
  const _savedShipDate = savedSettings.shipDate;
  const _today = getDateNDaysAhead(0);
  const [shipDate, setShipDate]             = useState(
    _savedShipDate && _savedShipDate >= _today ? _savedShipDate : getDateNDaysAhead(3)
  );
  const _savedBoxes = savedSettings.minBoxesFilter;
  const [minBoxesFilter, setMinBoxesFilter] = useState(
    _savedBoxes && typeof _savedBoxes === 'object' ? _savedBoxes : { min: '', max: '' }
  );
  const [showShipmentOnly, setShowShipmentOnly] = useState(false);
  const [showOosOnly, setShowOosOnly] = useState(false);
  const [sizeFilter, setSizeFilter] = useState(savedSettings.sizeFilter || '');

  // Кастомные диапазоны дат для Avg Daily Sales (7d/14d) и Actual Sales —
  // { v7: {from,to}, v14: {from,to}, actual: {from,to} }; пусто = дефолт
  // сервера (последние 7/14/30 дней по вчера). Данные — из amazon_day_sales.
  const [salesRanges, setSalesRanges] = useState(
    savedSettings.salesRanges && typeof savedSettings.salesRanges === 'object' ? savedSettings.salesRanges : {}
  );
  const [rangePopup, setRangePopup] = useState(null); // null | {which, from, to}

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        searchQuery, selectedBrands, selectedNiches, selectedTypes, shipDate, minBoxesFilter, sizeFilter, salesRanges,
      }));
    } catch {}
  }, [searchQuery, selectedBrands, selectedNiches, selectedTypes, shipDate, minBoxesFilter, sizeFilter, salesRanges]);

  const fetchBoxInputs = useCallback(async () => {
    try {
      const { data: rows } = await api.get(`/shipments-planning/box-inputs`);
      const map = {};
      rows.forEach(row => { map[row.sku] = row.boxes; });
      setBoxInputs(map);
    } catch {}
  }, []);

  const saveBoxInputs = useCallback(async () => {
    setBoxSaveStatus('saving');
    try {
      const inputs = {};
      Object.entries(boxInputs).forEach(([sku, val]) => {
        if (val !== '' && Number(val) > 0) inputs[sku] = Number(val);
      });
      await api.post(`/shipments-planning/box-inputs/save`, { inputs });
      setBoxSaveStatus('saved');
      setTimeout(() => setBoxSaveStatus(null), 2500);
    } catch {
      setBoxSaveStatus('error');
      setTimeout(() => setBoxSaveStatus(null), 3000);
    }
  }, [boxInputs]);

  const clearBoxInputs = useCallback(() => {
    setShowClearConfirm(true);
  }, []);

  const confirmClearBoxInputs = useCallback(async () => {
    setShowClearConfirm(false);
    try {
      await api.post(`/shipments-planning/box-inputs/clear`);
      setBoxInputs({});
    } catch {}
  }, []);

  // ── Amazon Upload Export ─────────────────────────────────────────────────
  const handleExportAmazonUpload = useCallback(async () => {
    // Only rows with box qty > 0
    const toShip = products.filter(p => {
      const v = boxInputs[p.SKU];
      return v !== undefined && v !== '' && Number(v) > 0;
    });
    if (toShip.length === 0) {
      alert('No products with planned boxes to export.');
      return;
    }

    // Fetch SKU mapping from DB
    let mappingBySkuOrgano = {};
    try {
      const { data } = await api.get(`/api/amazon_sku_mapping?limit=500`);
      (data.items || []).forEach(row => {
        mappingBySkuOrgano[row.sku_organo] = row;
      });
    } catch (e) {
      console.error('Failed to fetch amazon_sku_mapping', e);
    }

    // Expiration date = shipDate + 3 years, YYYY-DD-MM (Amazon format)
    const [sy, sm, sd] = shipDate.split('-').map(Number);
    const expDate = new Date(sy + 3, sm - 1, sd);
    const expStr = `${expDate.getFullYear()}-${String(expDate.getDate()).padStart(2, '0')}-${String(expDate.getMonth() + 1).padStart(2, '0')}`;

    // Data rows: columns match Amazon template A–I
    // A: Merchant SKU, B: Quantity, C: Expiration date, D: Units per box,
    // E: Number of boxes, F: Box length, G: Box width, H: Box height, I: Box weight
    const dataRows = toShip.map(p => {
      const boxes    = Number(boxInputs[p.SKU]);
      const bpb      = Number(p.bagsPerBox) || 0;
      const m        = mappingBySkuOrgano[p.SKU] || {};
      return [
        m.merchant_sku              || '',
        boxes * bpb,
        expStr,
        bpb,
        boxes,
        m.box_length_in != null ? Number(m.box_length_in) : '',
        m.box_width_in  != null ? Number(m.box_width_in)  : '',
        m.box_height_in != null ? Number(m.box_height_in) : '',
        m.box_weight_lb != null ? Number(m.box_weight_lb) : '',
      ];
    });

    // Try to load the Amazon template file from /public/
    let workbook;
    try {
      const tmplRes = await fetch('/amazon_shipment_template.xlsx');
      if (!tmplRes.ok) throw new Error('template not found');
      const buf = await tmplRes.arrayBuffer();
      workbook = XLSX.read(new Uint8Array(buf), { type: 'array', cellStyles: true, cellNF: true });
    } catch {
      alert('Template file not found.\nPlease put amazon_shipment_template.xlsx into the client/public/ folder.');
      return;
    }

    // Find the data sheet (first sheet, or "Create workflow – template")
    const sheetName = workbook.SheetNames.find(n => n.toLowerCase().includes('workflow')) || workbook.SheetNames[0];
    const ws = workbook.Sheets[sheetName];

    // Detect data start row: first row after header row 6 = row 7 (0-indexed: 6)
    const DATA_START_ROW = 7; // 1-based
    const COLS = ['A','B','C','D','E','F','G','H','I'];

    dataRows.forEach((row, i) => {
      const r = DATA_START_ROW + i;
      row.forEach((val, j) => {
        const cellRef = `${COLS[j]}${r}`;
        const isNum = typeof val === 'number';
        const existing = ws[cellRef];
        if (existing) {
          // Preserve borders/colors from template, just set value
          existing.v = val;
          existing.t = isNum ? 'n' : 's';
          existing.w = String(val);
        } else {
          // Row beyond template's pre-styled rows: copy style from row 7
          const tmplCell = ws[`${COLS[j]}7`];
          ws[cellRef] = {
            v: val,
            t: isNum ? 'n' : 's',
            w: String(val),
            ...(tmplCell && tmplCell.s ? { s: tmplCell.s } : {}),
          };
        }
      });
    });

    // Expand the sheet range to cover new rows
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:I6');
    range.e.r = Math.max(range.e.r, DATA_START_ROW - 1 + dataRows.length - 1);
    range.e.c = Math.max(range.e.c, COLS.length - 1);
    ws['!ref'] = XLSX.utils.encode_range(range);

    const fileName = `amazon_shipment_${shipDate}.xlsx`;
    XLSX.writeFile(workbook, fileName, { cellStyles: true });
  }, [products, boxInputs, shipDate]);

  const fetchTimestamps = useCallback(async () => {
    try {
      const { data: { fba_updated_at, awd_updated_at } } = await api.get(`/shipments-planning/stock-timestamps`);
      setFbaUpdatedAt(fba_updated_at || null);
      setAwdUpdatedAt(awd_updated_at || null);
    } catch {}
  }, []);

  const fetchVelocity = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (salesRanges.v7?.from && salesRanges.v7?.to) {
        p.set('from7', salesRanges.v7.from); p.set('to7', salesRanges.v7.to);
      }
      if (salesRanges.v14?.from && salesRanges.v14?.to) {
        p.set('from14', salesRanges.v14.from); p.set('to14', salesRanges.v14.to);
      }
      const qs = p.toString();
      const { data: rows } = await api.get(`/shipments-planning/velocity${qs ? '?' + qs : ''}`);
      const map = {};
      rows.forEach(row => { map[row.sku] = row; });
      setVelocityData(map);
    } catch {}
  }, [salesRanges.v7, salesRanges.v14]);

  const fetchActualSales = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (salesRanges.actual?.from && salesRanges.actual?.to) {
        p.set('from', salesRanges.actual.from); p.set('to', salesRanges.actual.to);
      }
      const qs = p.toString();
      const { data: rows } = await api.get(`/shipments-planning/actual-sales${qs ? '?' + qs : ''}`);
      const map = {};
      rows.forEach(row => { map[row.sku] = row; });
      setActualSalesData(map);
    } catch {}
  }, [salesRanges.actual]);

  // Рефетч при смене диапазона (и первичная загрузка) — отдельно от общего
  // mount-эффекта, чтобы смена дат не перегружала продукты
  useEffect(() => { fetchVelocity(); }, [fetchVelocity]);
  useEffect(() => { fetchActualSales(); }, [fetchActualSales]);

  // Попап выбора диапазона: префилл текущим кастомным или дефолтом по вчера
  const openRangePopup = useCallback((which) => {
    const defDays = which === 'v7' ? 7 : which === 'v14' ? 14 : 30;
    const cur = salesRanges[which];
    let from = cur?.from, to = cur?.to;
    if (!from || !to) {
      const t = new Date(); t.setDate(t.getDate() - 1);
      const f = new Date(t); f.setDate(f.getDate() - (defDays - 1));
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      from = fmt(f); to = fmt(t);
    }
    setRangePopup({ which, from, to });
  }, [salesRanges]);


  const reloadProducts = useCallback(async () => {
    try {
      const { data } = await api.get(`/shipments-planning/products`);
      setProducts(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  const fetchComments = useCallback(async () => {
    try {
      const { data: rows } = await api.get(`/shipments-planning/comments`);
      const map = {};
      rows.forEach(row => { map[row.sku] = row.comment; });
      setCommentsData(map);
    } catch {}
  }, []);

  const saveComment = useCallback(async (sku, comment) => {
    setCommentsData(prev => ({ ...prev, [sku]: comment }));
    try {
      await api.post(`/shipments-planning/comments/save`, { sku, comment });
    } catch {}
  }, []);

  // Manual stock edits resolve to false when the save failed — the cell then
  // snaps back to the value the table still calculates with
  const saveFbaStock = useCallback(async (asin, field, value) => {
    try {
      await api.post(`/shipments-planning/fba-stock-manual`, { asin, field, value: Number(value) });
      reloadProducts();
      fetchTimestamps();
    } catch { return false; }
  }, [reloadProducts, fetchTimestamps]);

  const saveAwdStock = useCallback(async (asin, value) => {
    try {
      await api.post(`/shipments-planning/awd-stock-manual`, { asin, value: Number(value) });
      reloadProducts();
      fetchTimestamps();
    } catch { return false; }
  }, [reloadProducts, fetchTimestamps]);

  // Forecast Sales: computed from Amazon monthly sales data (same logic as YoyMonthlyView)
  // — the fallback for products the AI agent has no forecast for
  const { forecastSalesData } = useAmazonMonthlyForecast(products);

  // AI-agent forecast, { ASIN: { '2026-09' | '2026-W40': { value, … } } }:
  // month → Forecast Sales column, week → Days Supplied / Forecast AI
  const [aiForecast, setAiForecast] = useState({ week: {}, month: {} });
  useEffect(() => {
    ['week', 'month'].forEach(period => {
      api.get(`/api/yoy-ai-forecast?period=${period}`)
        .then(({ data }) => {
          if (data?.success) setAiForecast(prev => ({ ...prev, [period]: data.ai || {} }));
        })
        .catch(err => console.error(`Error loading AI forecast (${period}):`, err));
    });
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [prodRes, brandRes, nicheRes] = await Promise.all([
          api.get(`/shipments-planning/products`),
          api.get(`/shipments-planning/brands`),
          api.get(`/shipments-planning/niches`),
        ]);
        const [prodData, brandData, nicheData] = [prodRes.data, brandRes.data, nicheRes.data];
        setProducts(Array.isArray(prodData) ? prodData : []);
        setBrands(Array.isArray(brandData) ? brandData : []);
        setNiches(Array.isArray(nicheData) ? nicheData : []);
      } catch (err) {
        console.error('Error loading shipments planning data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    fetchTimestamps();
    fetchBoxInputs();
    fetchComments();
  }, [fetchTimestamps, fetchBoxInputs, fetchComments]);

  useEffect(() => {
    // 5 days ending the day before shipDate (shipDate-5 .. shipDate-1)
    const [y, m, d] = shipDate.split('-').map(Number);
    const ship = new Date(y, m - 1, d);
    const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
    const fromDate = new Date(ship); fromDate.setDate(fromDate.getDate() - 5);
    const toDate   = new Date(ship); toDate.setDate(toDate.getDate() - 1);
    const from = fmt(fromDate);
    const to   = fmt(toDate);

    Promise.all([
      api.get(`/shipments-planning/daily-plan?from=${from}&to=${to}`).then(r => r.data),
      api.get(`/shipments-planning/produced?from=${from}&to=${to}`).then(r => r.data),
    ]).then(([planData, prodData]) => {
      setDailyPlan(Array.isArray(planData) ? planData : []);
      // Build lookup: { [sku]: { [date]: produced } }
      const map = {};
      if (Array.isArray(prodData)) {
        prodData.forEach(row => {
          if (!map[row.sku]) map[row.sku] = {};
          map[row.sku][row.date] = (map[row.sku][row.date] || 0) + Number(row.produced);
        });
      }
      setProducedData(map);
    }).catch(err => console.error('Error loading daily plan / produced:', err));
  }, [shipDate]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const q = searchQuery.toLowerCase();
      const matchSearch = !q ||
        p.name?.toLowerCase().includes(q) ||
        p.SKU?.toLowerCase().includes(q) ||
        p.ASIN?.toLowerCase().includes(q);
      const matchBrand = selectedBrands.length === 0 || selectedBrands.includes(String(p.brandID));
      const matchNiche = selectedNiches.length === 0 || selectedNiches.includes(String(p.niche_category_id));
      const matchType  = selectedTypes.length === 0  || selectedTypes.includes(p.type);
      const matchShipment = !showShipmentOnly ||
        (boxInputs[p.SKU] !== undefined && boxInputs[p.SKU] !== '' && Number(boxInputs[p.SKU]) > 0);
      const matchSize = sizeFilter === '' ||
        (p.pack_size_oz != null && Number(p.pack_size_oz) === Number(sizeFilter));
      const matchOos = !showOosOnly || (p.ASIN && Number(p.stock_fba) === 0);
      return matchSearch && matchBrand && matchNiche && matchType && matchShipment && matchSize && matchOos;
    });
  }, [products, searchQuery, selectedBrands, selectedNiches, selectedTypes, showShipmentOnly, boxInputs, sizeFilter, showOosOnly]);

  // Distinct pack sizes (microgreens) for the toolbar filter
  const packSizes = useMemo(() => {
    const set = new Set();
    products.forEach(p => { if (p.pack_size_oz != null) set.add(Number(p.pack_size_oz)); });
    return Array.from(set).sort((a, b) => a - b);
  }, [products]);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedBrands([]);
    setSelectedNiches([]);
    setSelectedTypes([]);
    setMinBoxesFilter({ min: '', max: '' });
    setShowShipmentOnly(false);
    setShowOosOnly(false);
    setSizeFilter('');
  }, []);

  const isBoxesFilterActive = minBoxesFilter.min !== '' || minBoxesFilter.max !== '';
  const hasActiveFilters = searchQuery || selectedBrands.length > 0 || selectedNiches.length > 0 || selectedTypes.length > 0 || isBoxesFilterActive || showShipmentOnly || showOosOnly || sizeFilter !== '';

  return (
    <div className="sp-root">
      <HeaderLogged />
      <div className="sp-container">

        <div className="sp-page-header">
          <div>
            <h2 className="sp-title">Shipments Planning</h2>
            <p className="sp-subtitle">Plan marketplace shipments by product across all product lines</p>
          </div>
        </div>

        {/* Marketplace tabs */}
        <div className="sp-mp-tabs">
          <button
            className={`sp-mp-tab${activeTab === 'amazon' ? ' active' : ''}`}
            onClick={() => switchTab('amazon')}
          >
            Amazon
          </button>
          <button
            className={`sp-mp-tab${activeTab === 'walmart' ? ' active' : ''}`}
            onClick={() => switchTab('walmart')}
          >
            Walmart
          </button>
        </div>

        {activeTab === 'walmart' && <WalmartShipmentsTab />}

        {activeTab === 'amazon' && <>
        {/* manual FBA/AWD upload buttons hidden 31.07.2026 — stock syncs from
            SP-API 3x/day; onOpenFbaPopup/onOpenAwdPopup deliberately not passed */}
        <ShipmentsToolbar
          searchQuery={searchQuery}       setSearchQuery={setSearchQuery}
          selectedBrands={selectedBrands} setSelectedBrands={setSelectedBrands}
          selectedNiches={selectedNiches} setSelectedNiches={setSelectedNiches}
          selectedTypes={selectedTypes}   setSelectedTypes={setSelectedTypes}
          brands={brands}
          niches={niches}
          filteredCount={filteredProducts.length}
          totalCount={products.length}
          hasActiveFilters={hasActiveFilters}
          clearFilters={clearFilters}
          shipDate={shipDate}
          setShipDate={setShipDate}
          onOpenFbaPopup={undefined}
          onOpenAwdPopup={undefined}
          onExportAmazonUpload={handleExportAmazonUpload}
          onOpenSkuMapping={() => setShowSkuMappingPopup(true)}
          fbaUpdatedAt={fbaUpdatedAt}
          awdUpdatedAt={awdUpdatedAt}
          minBoxesFilter={minBoxesFilter}
          setMinBoxesFilter={setMinBoxesFilter}
          showShipmentOnly={showShipmentOnly}
          setShowShipmentOnly={setShowShipmentOnly}
          showOosOnly={showOosOnly}
          setShowOosOnly={setShowOosOnly}
          sizeFilter={sizeFilter}
          setSizeFilter={setSizeFilter}
          packSizes={packSizes}
        />

        {loading ? (
          <div className="sp-loading">
            <i className="fas fa-circle-notch sp-spin"></i>
            <span>Loading products…</span>
          </div>
        ) : (
          <ShipmentsTable
            products={filteredProducts}
            dailyPlan={dailyPlan}
            producedData={producedData}
            shipDate={shipDate}
            boxInputs={boxInputs}
            setBoxInputs={setBoxInputs}
            fbaUpdatedAt={fbaUpdatedAt}
            awdUpdatedAt={awdUpdatedAt}
            velocityData={velocityData}
            actualSalesData={actualSalesData}
            onOpenRangePopup={openRangePopup}
            forecastSalesData={forecastSalesData}
            aiForecast={aiForecast}
            minBoxesFilter={minBoxesFilter}
            onSaveBoxInputs={saveBoxInputs}
            onClearBoxInputs={clearBoxInputs}
            boxSaveStatus={boxSaveStatus}
            commentsData={commentsData}
            onSaveComment={saveComment}
            onSaveFbaStock={saveFbaStock}
            onSaveAwdStock={saveAwdStock}
          />
        )}
        </>}
      </div>

      {/* FBA Stock Upload Popup */}
      <YoyAmzStockPopup
        showStockUploadPopup={showStockUploadPopup}
        setShowStockUploadPopup={setShowStockUploadPopup}
        selectedStockFile={selectedStockFile}
        setSelectedStockFile={setSelectedStockFile}
        parsedStockData={parsedStockData}
        setParsedStockData={setParsedStockData}
        uploadStockStatus={uploadStockStatus}
        setUploadStockStatus={setUploadStockStatus}
        stockFileInputRef={stockFileInputRef}
        handleStockFileUpload={handleStockFileUpload}
        handleStockUploadSubmit={handleStockUploadSubmit}
        onSyncSuccess={() => { fetchTimestamps(); reloadProducts(); }}
      />

      {/* AWD Upload Popup */}
      <YoyAwdUploadPopup
        showAwdUploadPopup={awdUpload.showAwdUploadPopup}
        setShowAwdUploadPopup={awdUpload.setShowAwdUploadPopup}
        awdFileInputRef={awdUpload.awdFileInputRef}
        selectedAwdFile={awdUpload.selectedAwdFile}
        setSelectedAwdFile={awdUpload.setSelectedAwdFile}
        parsedAwdData={awdUpload.parsedAwdData}
        setParsedAwdData={awdUpload.setParsedAwdData}
        uploadAwdStatus={awdUpload.uploadAwdStatus}
        setUploadAwdStatus={awdUpload.setUploadAwdStatus}
        handleAwdFileUpload={awdUpload.handleAwdFileUpload}
        handleAwdUploadSubmit={awdUpload.handleAwdUploadSubmit}
      />

      {/* SKU Mapping & Box Settings */}
      <AmazonSkuMappingPopup
        show={showSkuMappingPopup}
        onClose={() => setShowSkuMappingPopup(false)}
      />

      {/* Clear boxes confirmation */}
      {showClearConfirm && (
        <ConfirmPopup
          title="Clear all box quantities?"
          message="All planned box quantities will be removed from the database. This cannot be undone."
          confirmLabel="Yes, clear all"
          cancelLabel="Cancel"
          danger
          onConfirm={confirmClearBoxInputs}
          onCancel={() => setShowClearConfirm(false)}
        />
      )}

      {/* Custom date range for sales columns (daily Amazon data) */}
      {rangePopup && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setRangePopup(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: 8, padding: 20, minWidth: 330, boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 4px' }}>{RANGE_TITLES[rangePopup.which]}</h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#666' }}>
              Custom date range — daily sales from the Amazon API. The average and
              days-of-stock recalculate from the chosen period.
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
              <input type="date" value={rangePopup.from}
                onChange={e => setRangePopup(p => ({ ...p, from: e.target.value }))} />
              <span>—</span>
              <input type="date" value={rangePopup.to}
                onChange={e => setRangePopup(p => ({ ...p, to: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                style={{ padding: '8px 14px', borderRadius: 4, border: '1px solid #ccc', background: '#f5f5f5', cursor: 'pointer' }}
                onClick={() => {
                  setSalesRanges(prev => { const n = { ...prev }; delete n[rangePopup.which]; return n; });
                  setRangePopup(null);
                }}
              >
                Reset to default
              </button>
              <button
                style={{ padding: '8px 14px', borderRadius: 4, border: 'none', background: '#007bff', color: '#fff', cursor: 'pointer' }}
                disabled={!rangePopup.from || !rangePopup.to || rangePopup.from > rangePopup.to}
                onClick={() => {
                  setSalesRanges(prev => ({ ...prev, [rangePopup.which]: { from: rangePopup.from, to: rangePopup.to } }));
                  setRangePopup(null);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ShipmentsPlanningPage;